// Mémoire CxO - Page de visualisation et gestion
// Direction IA → Mémoire CxO

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Target,
  FileCheck,
  Zap,
  AlertTriangle,
  History,
  RefreshCw,
  Archive,
  Plus,
  Clock,
  User,
  CheckCircle2,
  XCircle,
  Pause,
  MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par rôle CxO
const CXO_COLORS = {
  cmo: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", badge: "bg-blue-500" },
  cto: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", badge: "bg-red-500" },
  coo: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", badge: "bg-emerald-500" }
};

const CXO_LABELS = {
  cmo: "CMO IA — Marketing",
  cto: "CTO IA — Technique",
  coo: "COO IA — Opérations"
};

// Composant pour un objectif
const ObjectifItem = ({ objectif, rôle, onStatusChange }) => {
  const statusColors = {
    actif: "bg-emerald-500",
    atteint: "bg-blue-500",
    abandonné: "bg-red-500",
    en_pause: "bg-yellow-500"
  };

  return (
    <div className="p-4 bg-zinc-800/50 rounded-lg border border-white/5 flex justify-between items-start gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`${statusColors[objectif.statut]} text-white text-xs`}>
            {objectif.statut}
          </Badge>
          <span className="text-xs text-zinc-500">{objectif.id}</span>
        </div>
        <p className="text-zinc-200">{objectif.description}</p>
        {objectif.notes && (
          <p className="text-sm text-zinc-400 mt-2 italic">{objectif.notes}</p>
        )}
        <p className="text-xs text-zinc-500 mt-2">
          <Clock className="w-3 h-3 inline mr-1" />
          {new Date(objectif.created_at).toLocaleDateString('fr-FR')}
        </p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-zinc-800 border-white/10">
          <DropdownMenuItem onClick={() => onStatusChange(objectif.id, "actif")} className="text-emerald-400">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Actif
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onStatusChange(objectif.id, "atteint")} className="text-blue-400">
            <Target className="w-4 h-4 mr-2" /> Atteint
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onStatusChange(objectif.id, "en_pause")} className="text-yellow-400">
            <Pause className="w-4 h-4 mr-2" /> En pause
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onStatusChange(objectif.id, "abandonné")} className="text-red-400">
            <XCircle className="w-4 h-4 mr-2" /> Abandonné
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Composant pour une décision
const DecisionItem = ({ decision, rôle, onStatusChange }) => {
  const statusColors = {
    active: "bg-emerald-500",
    obsolète: "bg-yellow-500",
    archivée: "bg-zinc-500"
  };

  return (
    <div className="p-4 bg-zinc-800/50 rounded-lg border border-white/5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${statusColors[decision.statut]} text-white text-xs`}>
              {decision.statut}
            </Badge>
            {decision.validee_par && (
              <span className="text-xs text-zinc-400 flex items-center">
                <User className="w-3 h-3 mr-1" />
                {decision.validee_par}
              </span>
            )}
          </div>
          <p className="text-zinc-200 font-medium">{decision.decision}</p>
          {decision.contexte && (
            <p className="text-sm text-zinc-400 mt-2">Contexte: {decision.contexte}</p>
          )}
          <p className="text-xs text-zinc-500 mt-2">
            <Clock className="w-3 h-3 inline mr-1" />
            {new Date(decision.date).toLocaleDateString('fr-FR')}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-zinc-800 border-white/10">
            <DropdownMenuItem onClick={() => onStatusChange(decision.id, "active")} className="text-emerald-400">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Active
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(decision.id, "obsolète")} className="text-yellow-400">
              <AlertTriangle className="w-4 h-4 mr-2" /> Obsolète
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(decision.id, "archivée")} className="text-zinc-400">
              <Archive className="w-4 h-4 mr-2" /> Archiver
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Composant pour une action
const ActionItem = ({ action, rôle, onStatusChange }) => {
  const statusColors = {
    planifiée: "bg-zinc-500",
    en_cours: "bg-blue-500",
    terminée: "bg-emerald-500",
    annulée: "bg-red-500"
  };

  return (
    <div className="p-4 bg-zinc-800/50 rounded-lg border border-white/5 flex justify-between items-start gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`${statusColors[action.etat]} text-white text-xs`}>
            {action.etat}
          </Badge>
          <span className="text-xs text-zinc-500 font-mono">{action.type}</span>
        </div>
        <p className="text-zinc-200">{action.description}</p>
        {action.lie_a && (
          <p className="text-xs text-zinc-400 mt-1">Lié à: {action.lie_a}</p>
        )}
        <p className="text-xs text-zinc-500 mt-2">
          <Clock className="w-3 h-3 inline mr-1" />
          {new Date(action.created_at).toLocaleDateString('fr-FR')}
        </p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-zinc-800 border-white/10">
          <DropdownMenuItem onClick={() => onStatusChange(action.id, "en_cours")} className="text-blue-400">
            <Zap className="w-4 h-4 mr-2" /> En cours
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onStatusChange(action.id, "terminée")} className="text-emerald-400">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Terminée
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onStatusChange(action.id, "annulée")} className="text-red-400">
            <XCircle className="w-4 h-4 mr-2" /> Annulée
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Composant pour une contrainte
const ContrainteItem = ({ contrainte, rôle, onToggle }) => {
  return (
    <div className={`p-4 rounded-lg border flex justify-between items-center gap-4 ${
      contrainte.active 
        ? "bg-amber-500/10 border-amber-500/30" 
        : "bg-zinc-800/30 border-white/5 opacity-50"
    }`}>
      <div className="flex items-center gap-3">
        <AlertTriangle className={`w-5 h-5 ${contrainte.active ? "text-amber-400" : "text-zinc-500"}`} />
        <p className={contrainte.active ? "text-zinc-200" : "text-zinc-500 line-through"}>
          {contrainte.description}
        </p>
      </div>
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => onToggle(contrainte.id)}
        className={contrainte.active ? "text-amber-400" : "text-zinc-500"}
      >
        {contrainte.active ? "Désactiver" : "Activer"}
      </Button>
    </div>
  );
};

// Composant principal
export default function CxoMemoryPage() {
  const [selectedRole, setSelectedRole] = useState("cmo");
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Charger la mémoire
  const loadMemory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/cxo-memory/${selectedRole}`);
      setMemory(res.data);
    } catch (err) {
      console.error("Erreur chargement mémoire:", err);
      toast.error("Erreur lors du chargement de la mémoire");
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => {
    loadMemory();
  }, [loadMemory]);

  // Changer le statut d'un objectif
  const handleObjectifStatusChange = async (id, statut) => {
    try {
      await axios.patch(`${API}/api/cxo-memory/${selectedRole}/objectif/${id}`, { statut });
      toast.success("Objectif mis à jour");
      loadMemory();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Changer le statut d'une décision
  const handleDecisionStatusChange = async (id, statut) => {
    try {
      await axios.patch(`${API}/api/cxo-memory/${selectedRole}/decision/${id}`, { statut });
      toast.success("Décision mise à jour");
      loadMemory();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Changer l'état d'une action
  const handleActionStatusChange = async (id, etat) => {
    try {
      await axios.patch(`${API}/api/cxo-memory/${selectedRole}/action/${id}`, { statut: etat });
      toast.success("Action mise à jour");
      loadMemory();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Toggle contrainte
  const handleContrainteToggle = async (id) => {
    try {
      await axios.patch(`${API}/api/cxo-memory/${selectedRole}/contrainte/${id}/toggle`);
      toast.success("Contrainte mise à jour");
      loadMemory();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Ajouter une note stratégique
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/api/cxo-memory/${selectedRole}/note`, { note: newNote.trim() });
      toast.success("Note ajoutée à l'historique");
      setNewNote("");
      setAddNoteOpen(false);
      loadMemory();
    } catch (err) {
      toast.error("Erreur lors de l'ajout de la note");
    } finally {
      setSavingNote(false);
    }
  };

  const colors = CXO_COLORS[selectedRole];
  
  // Stats
  const stats = memory ? {
    objectifs: memory.objectifs?.filter(o => o.statut === "actif").length || 0,
    decisions: memory.decisions?.filter(d => d.statut === "active").length || 0,
    actions: memory.actions?.filter(a => ["planifiée", "en_cours"].includes(a.etat)).length || 0,
    contraintes: memory.contraintes?.filter(c => c.active).length || 0
  } : { objectifs: 0, decisions: 0, actions: 0, contraintes: 0 };

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Mémoire CxO</h1>
          <p className="text-zinc-400">
            Source de vérité persistante — indépendante des conversations
          </p>
        </div>

        {/* Sélection du rôle */}
        <div className="flex gap-3 mb-6">
          {Object.entries(CXO_LABELS).map(([rôle, label]) => (
            <Button
              key={rôle}
              onClick={() => setSelectedRole(rôle)}
              className={`px-6 py-3 rounded-lg transition-all ${
                selectedRole === rôle
                  ? `${CXO_COLORS[rôle].bg} ${CXO_COLORS[rôle].border} border ${CXO_COLORS[rôle].text}`
                  : "bg-zinc-800/50 border border-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              {label}
            </Button>
          ))}
          
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              onClick={loadMemory}
              className="border-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={() => setAddNoteOpen(true)}
              className={`${colors.bg} ${colors.text} border ${colors.border}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Note stratégique
            </Button>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className={`${colors.bg} border ${colors.border}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <Target className={`w-8 h-8 ${colors.text}`} />
              <div>
                <p className="text-2xl font-bold text-white">{stats.objectifs}</p>
                <p className="text-sm text-zinc-400">Objectifs actifs</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`${colors.bg} border ${colors.border}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <FileCheck className={`w-8 h-8 ${colors.text}`} />
              <div>
                <p className="text-2xl font-bold text-white">{stats.decisions}</p>
                <p className="text-sm text-zinc-400">Décisions actives</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`${colors.bg} border ${colors.border}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <Zap className={`w-8 h-8 ${colors.text}`} />
              <div>
                <p className="text-2xl font-bold text-white">{stats.actions}</p>
                <p className="text-sm text-zinc-400">Actions en cours</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`${colors.bg} border ${colors.border}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className={`w-8 h-8 ${colors.text}`} />
              <div>
                <p className="text-2xl font-bold text-white">{stats.contraintes}</p>
                <p className="text-sm text-zinc-400">Contraintes actives</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contenu principal */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-zinc-400 animate-spin" />
          </div>
        ) : memory ? (
          <Tabs defaultValue="objectifs" className="w-full">
            <TabsList className="bg-zinc-800/50 border border-white/5 mb-6">
              <TabsTrigger value="objectifs" className="data-[state=active]:bg-zinc-700">
                <Target className="w-4 h-4 mr-2" />
                Objectifs ({memory.objectifs?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="decisions" className="data-[state=active]:bg-zinc-700">
                <FileCheck className="w-4 h-4 mr-2" />
                Décisions ({memory.decisions?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="actions" className="data-[state=active]:bg-zinc-700">
                <Zap className="w-4 h-4 mr-2" />
                Actions ({memory.actions?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="contraintes" className="data-[state=active]:bg-zinc-700">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Contraintes ({memory.contraintes?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="historique" className="data-[state=active]:bg-zinc-700">
                <History className="w-4 h-4 mr-2" />
                Historique
              </TabsTrigger>
            </TabsList>

            {/* Objectifs */}
            <TabsContent value="objectifs">
              <Card className="bg-zinc-900/50 border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className={colors.text} />
                    Objectifs {selectedRole.toUpperCase()}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {memory.objectifs?.length > 0 ? (
                    memory.objectifs.map(obj => (
                      <ObjectifItem 
                        key={obj.id} 
                        objectif={obj} 
                        rôle={selectedRole}
                        onStatusChange={handleObjectifStatusChange}
                      />
                    ))
                  ) : (
                    <p className="text-zinc-500 text-center py-8">
                      Aucun objectif défini. Les objectifs sont créés via le chat CxO.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Décisions */}
            <TabsContent value="decisions">
              <Card className="bg-zinc-900/50 border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileCheck className={colors.text} />
                    Décisions validées
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {memory.decisions?.length > 0 ? (
                    memory.decisions.map(dec => (
                      <DecisionItem 
                        key={dec.id} 
                        decision={dec} 
                        rôle={selectedRole}
                        onStatusChange={handleDecisionStatusChange}
                      />
                    ))
                  ) : (
                    <p className="text-zinc-500 text-center py-8">
                      Aucune décision enregistrée. Les décisions sont validées via le chat CxO.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Actions */}
            <TabsContent value="actions">
              <Card className="bg-zinc-900/50 border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className={colors.text} />
                    Actions tracées
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {memory.actions?.length > 0 ? (
                    memory.actions.map(act => (
                      <ActionItem 
                        key={act.id} 
                        action={act} 
                        rôle={selectedRole}
                        onStatusChange={handleActionStatusChange}
                      />
                    ))
                  ) : (
                    <p className="text-zinc-500 text-center py-8">
                      Aucune action tracée. Les actions sont créées automatiquement via le chat CxO.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contraintes */}
            <TabsContent value="contraintes">
              <Card className="bg-zinc-900/50 border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className={colors.text} />
                    Contraintes structurelles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {memory.contraintes?.map(ctr => (
                    <ContrainteItem 
                      key={ctr.id} 
                      contrainte={ctr} 
                      rôle={selectedRole}
                      onToggle={handleContrainteToggle}
                    />
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Historique */}
            <TabsContent value="historique">
              <Card className="bg-zinc-900/50 border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className={colors.text} />
                    Historique des événements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {memory.historique?.slice().reverse().map((evt, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-3 p-3 bg-zinc-800/30 rounded-lg border border-white/5"
                      >
                        <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-zinc-500" />
                        <div className="flex-1">
                          <p className="text-zinc-300">{evt.event}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            {new Date(evt.timestamp).toLocaleString('fr-FR')} — {evt.source}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : null}

        {/* Dialog ajout note */}
        <Dialog open={addNoteOpen} onOpenChange={setAddNoteOpen}>
          <DialogContent className="bg-zinc-900 border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Ajouter une note stratégique</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Cette note sera ajoutée à l'historique du {selectedRole.toUpperCase()}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Votre note stratégique..."
                className="bg-zinc-800 border-white/10 text-white min-h-[100px]"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAddNoteOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleAddNote} 
                disabled={savingNote || !newNote.trim()}
                className={`${colors.bg} ${colors.text}`}
              >
                {savingNote ? "Enregistrement..." : "Ajouter"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Footer info */}
        <div className="mt-8 text-center text-xs text-zinc-600">
          La mémoire est indépendante des conversations. Un reset de chat ne fait rien perdre.
        </div>
      </div>
    </div>
  );
}
